// components/taxonomy/SubcategoryPickerModal.tsx (UPDATED: refresh hook + consistent normalized search)
//
// ✅ Uses SubcategoryDoc.nameNormalized consistently
// ✅ After suggesting, calls optional onAfterChange() so parent can reload Firestore-approved subcategories
// ✅ Keeps your allowPendingAdd behavior

import React, { useMemo, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  FlatList,
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { db } from "@/lib/firebase";
import { normalizeLabel, suggestSubcategory, type SubcategoryDoc } from "@/lib/taxonomy";

type Props = {
  visible: boolean;
  onClose: () => void;

  categoryId: string | null; // may be an approved or pending categoryId
  categoryName?: string;

  subcategories: SubcategoryDoc[];
  loading?: boolean;

  createdByUid: string;

  selectedSubcategoryId?: string | null;

  onSelectApproved: (subcategory: { id: string; name: string }) => void;
  onSelectPending: (subcategory: { id?: string | null; name: string }) => void;

  /**
   * Default: false (safer).
   * Set true only if you want to allow suggesting new subcategories.
   */
  allowPendingAdd?: boolean;

  /** optional: parent can refresh Firestore list after changes */
  onAfterChange?: () => Promise<void> | void;
};

function getNormalizedForSearch(s: SubcategoryDoc): string {
  // ✅ new schema
  if (typeof s.nameNormalized === "string" && s.nameNormalized) return s.nameNormalized;

  // Backward-compatible fallback if something old is in memory
  const maybeOld = s as unknown as { normalized?: string };
  if (typeof maybeOld.normalized === "string" && maybeOld.normalized) return maybeOld.normalized;

  return normalizeLabel(s.name);
}

export default function SubcategoryPickerModal({
  visible,
  onClose,
  categoryId,
  categoryName,
  subcategories,
  loading,
  createdByUid,
  selectedSubcategoryId,
  onSelectApproved,
  onSelectPending,
  allowPendingAdd,
  onAfterChange,
}: Props) {
  const insets = useSafeAreaInsets();
  const [search, setSearch] = useState("");

  const rawSearch = search.trim();
  const normalizedSearch = useMemo(() => normalizeLabel(rawSearch), [rawSearch]);

  const filtered = useMemo(() => {
    if (!normalizedSearch) return subcategories;
    return subcategories.filter((s) => getNormalizedForSearch(s).includes(normalizedSearch));
  }, [subcategories, normalizedSearch]);

  const exactMatchExists = useMemo(() => {
    if (!normalizedSearch) return false;
    return subcategories.some((s) => s.id === normalizedSearch || getNormalizedForSearch(s) === normalizedSearch);
  }, [subcategories, normalizedSearch]);

  const canSuggest = (allowPendingAdd ?? false) && !!normalizedSearch && !exactMatchExists;
  const keyboardBehavior =
    Platform.OS === "ios"
      ? "padding"
      : Platform.OS === "android"
      ? "height"
      : undefined;
  const sheetBottomPadding = Math.max(insets.bottom, 16);
  const listBottomPadding = sheetBottomPadding + 56;

  function handleClose() {
    Keyboard.dismiss();
    onClose();
  }

  async function handleAddNew() {
    if (!normalizedSearch) return;

    if (!categoryId) {
      Alert.alert("Select category first", "Pick a category first.");
      return;
    }

    Keyboard.dismiss();

    try {
      const res = await suggestSubcategory(db, {
        categoryId,
        categoryName,
        name: rawSearch,
        createdBy: createdByUid,
      });

      if (res.kind === "existsApproved") {
        onSelectApproved({ id: res.subcategoryId, name: res.subcategoryName });
        handleClose();
        return;
      }

      // Pending suggestion created/exists
      onSelectPending({ id: res.subcategoryId, name: res.subcategoryName });
      handleClose();

      Alert.alert(
        "Submitted for approval",
        `"${rawSearch}" will be reviewed. Your card can still be published.`
      );

      await Promise.resolve(onAfterChange?.());
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e ? String((e as any).message) : "Failed to add subcategory.";
      Alert.alert("Error", msg);
    }
  }

  function handlePick(item: SubcategoryDoc) {
    // ✅ id is subId only, consistent with Firestore doc id
    Keyboard.dismiss();
    onSelectApproved({ id: item.id, name: item.name });
    handleClose();
  }

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      statusBarTranslucent
      onRequestClose={handleClose}
    >
      <KeyboardAvoidingView
        behavior={keyboardBehavior}
        style={styles.keyboardAvoider}
      >
        <View style={styles.backdrop}>
          <View style={[styles.card, { paddingBottom: sheetBottomPadding }]}>
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>Select Subcategory</Text>
              {categoryName ? <Text style={styles.subtitle}>Category: {categoryName}</Text> : null}
            </View>

            <Pressable onPress={handleClose} style={styles.closeBtn} hitSlop={10}>
              <Text style={styles.closeText}>Close</Text>
            </Pressable>
          </View>

          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search subcategory…"
            style={styles.input}
            autoCapitalize="words"
            autoCorrect={false}
          />

          {loading ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator />
              <Text style={styles.loadingText}>Loading subcategories…</Text>
            </View>
          ) : (
            <>
              {canSuggest ? (
                <Pressable onPress={handleAddNew} style={styles.addBtn}>
                  <Text style={styles.addBtnText}>Add “{rawSearch}”</Text>
                  <Text style={styles.addSubText}>(Will be pending approval)</Text>
                </Pressable>
              ) : null}

              <FlatList
                data={filtered}
                keyExtractor={(item) => item.id}
                keyboardShouldPersistTaps="handled"
                style={styles.list}
                contentContainerStyle={[
                  styles.listContent,
                  { paddingBottom: listBottomPadding },
                ]}
                renderItem={({ item }) => {
                  const selected = selectedSubcategoryId === item.id;
                  return (
                    <Pressable
                      onPress={() => handlePick(item)}
                      style={[styles.row, selected && styles.rowSelected]}
                    >
                      <Text style={styles.rowText}>{item.name}</Text>
                      {selected ? <Text style={styles.badge}>Selected</Text> : null}
                    </Pressable>
                  );
                }}
                ListEmptyComponent={
                  <View style={styles.empty}>
                    <Text style={styles.emptyText}>No subcategories found.</Text>
                  </View>
                }
              />
            </>
          )}
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  keyboardAvoider: {
    flex: 1,
  },
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "flex-end",
  },
  card: {
    backgroundColor: "white",
    padding: 16,
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    maxHeight: "85%",
  },
  list: {
    flexGrow: 0,
  },
  listContent: {
    paddingTop: 0,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
  },
  title: { fontSize: 18, fontWeight: "700" },
  subtitle: { marginTop: 2, opacity: 0.7 },

  closeBtn: { paddingVertical: 6, paddingHorizontal: 10 },
  closeText: { fontSize: 14, fontWeight: "600" },

  input: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 10,
  },

  addBtn: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
  },
  addBtnText: { fontSize: 16, fontWeight: "700" },
  addSubText: { marginTop: 2, fontSize: 12, opacity: 0.7 },

  row: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  rowSelected: { backgroundColor: "#f6f6f6" },
  rowText: { fontSize: 15 },

  badge: { fontSize: 12, fontWeight: "700", opacity: 0.7 },

  empty: { padding: 18 },
  emptyText: { opacity: 0.7 },

  loadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 10,
  },
  loadingText: { opacity: 0.7 },
});
